import { useState, useEffect } from 'react';
import {
    CheckCircle2,
    XCircle,
    AlertTriangle,
    ChevronDown,
    ChevronRight,
    FileText,
    Phone,
    MapPin,
    Store,
    Copy,
    ExternalLink,
    Eye,
    Globe,
    Info,
    EyeOff,
    Target
} from 'lucide-react';
import { formatOfferText } from '../../../utils/textUtils';
import { authenticatedFetch } from '../../../utils/authenticatedFetch';
import OffersValidation from '../../validationResults/validations/OffersValidation.jsx';
import FinePrintValidation from '../../validationResults/validations/FinePrintValidation.jsx';
import TrackingNumbersValidation from '../../validationResults/validations/TrackingNumbersValidation.jsx';
import StoreAddressValidation from '../../validationResults/validations/StoreAddressValidation.jsx';
import CountiesValidation from '../../validationResults/validations/CountiesValidation.jsx';

export default function ValidationTab({
    store,
    validationResults,
    expandedPages,
    togglePage,
    ignoredOffers,
    ignoreOffer,
    restoreOffer,
    isOfferIgnored,
    copyToClipboard
}) {
    const [hiddenOffersByPage, setHiddenOffersByPage] = useState({});
    const [isLoadingHiddenOffers, setIsLoadingHiddenOffers] = useState(true);

    const companyId = store?._id || store?.id || store?.name;
    const companyName = store?.name;

    // Fetch hidden offers for all pages when component mounts
    useEffect(() => {
        async function fetchAllHiddenOffers() {
            if (!companyId) {
                setIsLoadingHiddenOffers(false);
                return;
            }

            try {
                setIsLoadingHiddenOffers(true);
                const response = await authenticatedFetch(`/api/stores/${encodeURIComponent(companyId)}/hidden-offers`);
                const data = await response.json();

                if (data.success) {
                    // Group hidden offers by page URL
                    const groupedByPage = {};
                    data.hiddenOffers.forEach(offer => {
                        if (!groupedByPage[offer.pageUrl]) {
                            groupedByPage[offer.pageUrl] = [];
                        }
                        groupedByPage[offer.pageUrl].push(offer);
                    });
                    setHiddenOffersByPage(groupedByPage);
                }
            } catch (error) {
                console.error('Error fetching hidden offers:', error);
            } finally {
                setIsLoadingHiddenOffers(false);
            }
        }

        fetchAllHiddenOffers();
    }, [companyId]);

    const handleHideOffer = async (pageUrl, offerText, offerType) => {
        try {
            const response = await authenticatedFetch(`/api/stores/${encodeURIComponent(companyId)}/hidden-offers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    companyName,
                    pageUrl,
                    offerText,
                    offerType: offerType || 'regular',
                    notes: `Hidden from ${pageUrl} validation`
                })
            });

            const data = await response.json();

            if (data.success) {
                // Update local state immediately
                setHiddenOffersByPage(prev => ({
                    ...prev,
                    [pageUrl]: [...(prev[pageUrl] || []), {
                        offerText,
                        offerType,
                        pageUrl,
                        companyId,
                        companyName,
                        hiddenAt: new Date().toISOString()
                    }]
                }));

                console.log('✅ Offer hidden successfully');
            } else {
                console.error('Failed to hide offer:', data.error);
                alert('Failed to hide offer. Please try again.');
            }
        } catch (error) {
            console.error('Error hiding offer:', error);
            alert('Error hiding offer. Please check console for details.');
        }
    };

    const handleUnhideOffer = async (pageUrl, offerText) => {
        try {
            const response = await authenticatedFetch(`/api/stores/${encodeURIComponent(companyId)}/hidden-offers`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    pageUrl,
                    offerText
                })
            });

            const data = await response.json();

            if (data.success) {
                // Update local state immediately
                setHiddenOffersByPage(prev => ({
                    ...prev,
                    [pageUrl]: (prev[pageUrl] || []).filter(
                        offer => offer.offerText !== offerText
                    )
                }));

                console.log('✅ Offer unhidden successfully');
            } else {
                console.error('Failed to unhide offer:', data.error);
                alert('Failed to unhide offer. Please try again.');
            }
        } catch (error) {
            console.error('Error unhiding offer:', error);
            alert('Error unhiding offer. Please check console for details.');
        }
    };

    const getValidationStatusIcon = (status, score) => {
        if (status === 'error' || score < 50) {
            return <XCircle size={16} className="text-red-500" />;
        } else if (status === 'warning' || score < 80) {
            return <AlertTriangle size={16} className="text-amber-500" />;
        } else {
            return <CheckCircle2 size={16} className="text-emerald-500" />;
        }
    };

    if (!validationResults) {
        return (
            <div className="p-8 text-center text-slate-500">
                No validation results available
            </div>
        );
    }

    return (
        <div className="p-6 space-y-8">
            {/* Validation Overview Header */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl p-6 border border-slate-200/60">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                        <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-200/60">
                            <CheckCircle2 size={24} className="text-emerald-600" />
                        </div>
                        Validation Results
                    </h3>
                    <div className={`
            px-6 py-3 rounded-2xl text-lg font-bold shadow-sm border
            ${validationResults.overallScore >= 90 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            validationResults.overallScore >= 75 ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                validationResults.overallScore >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                    'bg-red-50 text-red-700 border-red-200'}
          `}>
                        {validationResults.overallScore}%
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white/80 rounded-xl p-4 border border-slate-200/60">
                        <div className="text-2xl font-bold text-emerald-600">
                            {validationResults.scrapingResults?.summary?.successful || 0}
                        </div>
                        <div className="text-sm text-slate-600 font-medium">Pages Validated</div>
                    </div>
                    <div className="bg-white/80 rounded-xl p-4 border border-slate-200/60">
                        <div className="text-2xl font-bold text-slate-700">
                            {Object.keys(validationResults.validations).length}
                        </div>
                        <div className="text-sm text-slate-600 font-medium">Total Pages</div>
                    </div>
                    <div className="bg-white/80 rounded-xl p-4 border border-slate-200/60">
                        <div className="text-2xl font-bold text-red-600">
                            {validationResults.issues?.length || 0}
                        </div>
                        <div className="text-sm text-slate-600 font-medium">Critical Issues</div>
                    </div>
                </div>
            </div>

            {/* Page-by-Page Results */}
            <div className="space-y-6">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Page Validation Details</h3>

                {Object.entries(validationResults.validations).map(([pageUrl, pageValidation]) => (
                    <div key={pageUrl} className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden transition-all duration-200 hover:shadow-md">
                        {/* Page Header */}
                        <div
                            className="flex items-center justify-between p-6 bg-gradient-to-r from-slate-50 to-slate-100/50 cursor-pointer hover:from-slate-100 hover:to-slate-50 transition-all duration-200"
                            onClick={() => togglePage(pageUrl)}
                        >
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-200/60 transition-transform duration-200 hover:scale-105">
                                        {expandedPages[pageUrl] ? <ChevronDown size={18} className="text-slate-600" /> : <ChevronRight size={18} className="text-slate-600" />}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-lg text-slate-900 mb-1">
                                            {pageValidation.pageInfo?.type || 'Unknown Page'}
                                        </h4>
                                        <div className="text-sm text-slate-500 font-medium truncate max-w-96">
                                            {pageUrl}
                                        </div>
                                    </div>
                                </div>
                                <div className={`
                  px-4 py-2 rounded-xl text-sm font-bold shadow-sm border flex items-center gap-2
                  ${pageValidation.overallScore >= 90 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                        pageValidation.overallScore >= 75 ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                            pageValidation.overallScore >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                'bg-red-50 text-red-700 border-red-200'}
                `}>
                                    {getValidationStatusIcon('', pageValidation.overallScore)}
                                    {pageValidation.overallScore}%
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(pageUrl);
                                    }}
                                    className="p-2 hover:bg-white/80 rounded-xl transition-colors duration-200 border border-slate-200/60 bg-white/60"
                                    title="Copy URL"
                                >
                                    <Copy size={16} className="text-slate-600" />
                                </button>
                                <a
                                    href={pageUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="p-2 hover:bg-white/80 rounded-xl transition-colors duration-200 border border-slate-200/60 bg-white/60"
                                    title="Open Page"
                                >
                                    <ExternalLink size={16} className="text-slate-600" />
                                </a>
                            </div>
                        </div>

                        {/* Page Details - This will be refactored into separate components in Step 3.4 */}
                        {expandedPages[pageUrl] && (
                            <div className="p-6 space-y-6 bg-slate-50/30">
                                {/* Redirect Validation */}
                                {pageValidation.validations?.redirects && (
                                    <div className={`p-5 rounded-2xl border-2 shadow-sm ${pageValidation.validations.redirects.score >= 90 ? 'bg-emerald-50 border-emerald-200' :
                                        pageValidation.validations.redirects.score >= 75 ? 'bg-blue-50 border-blue-200' :
                                            pageValidation.validations.redirects.score >= 50 ? 'bg-amber-50 border-amber-200' :
                                                'bg-red-50 border-red-200'
                                        }`}>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-200/60">
                                                <ExternalLink size={18} className="text-indigo-600" />
                                            </div>
                                            <div className="flex-1">
                                                <h5 className="font-bold text-lg text-slate-900">URL Navigation Check</h5>
                                                <p className="text-sm text-slate-600 font-medium">Redirect analysis and URL validation</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {getValidationStatusIcon(pageValidation.validations.redirects.status, pageValidation.validations.redirects.score)}
                                                <span className="font-bold text-lg">
                                                    {pageValidation.validations.redirects.score}%
                                                </span>
                                            </div>
                                        </div>

                                        <div className="bg-white/80 rounded-xl p-4 space-y-3 border border-slate-200/60">
                                            <div className="grid grid-cols-1 gap-3">
                                                <div className="flex items-start gap-3">
                                                    <span className="text-sm font-bold text-slate-700 min-w-20">Original:</span>
                                                    <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded-lg flex-1 break-all">
                                                        {pageValidation.validations.redirects.originalUrl}
                                                    </span>
                                                </div>
                                                <div className="flex items-start gap-3">
                                                    <span className="text-sm font-bold text-slate-700 min-w-20">Final:</span>
                                                    <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded-lg flex-1 break-all">
                                                        {pageValidation.validations.redirects.finalUrl}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 pt-2 border-t border-slate-200/60">
                                                <span className="text-sm font-bold text-slate-700">Status:</span>
                                                {pageValidation.validations.redirects.originalUrl === pageValidation.validations.redirects.finalUrl ? (
                                                    <div className="flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
                                                        <CheckCircle2 size={14} />
                                                        No redirect - URL stayed the same
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                                                        <AlertTriangle size={14} />
                                                        URL changed during navigation
                                                    </div>
                                                )}
                                            </div>

                                            {pageValidation.validations.redirects.redirectChain && pageValidation.validations.redirects.redirectChain.length > 1 && (
                                                <details className="mt-4">
                                                    <summary className="cursor-pointer text-slate-600 hover:text-slate-800 font-medium flex items-center gap-2 p-2 bg-slate-100 rounded-lg">
                                                        <Eye size={14} />
                                                        View Redirect Chain ({pageValidation.validations.redirects.redirectChain.length} steps)
                                                    </summary>
                                                    <div className="mt-3 bg-slate-50 p-4 rounded-xl border border-slate-200/60">
                                                        <div className="space-y-2">
                                                            {pageValidation.validations.redirects.redirectChain.map((url, idx) => (
                                                                <div key={idx} className="flex items-start gap-3">
                                                                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                                                        {idx + 1}
                                                                    </div>
                                                                    <span className={`font-mono text-sm break-all ${idx === 0 ? 'font-bold text-slate-800' :
                                                                        idx === pageValidation.validations.redirects.redirectChain.length - 1 ? 'font-bold text-blue-700' :
                                                                            'text-slate-600'
                                                                        }`}>
                                                                        {url}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </details>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Validation Categories Grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Offers Validation */}
                                    {pageValidation.validations?.offers && (
                                        <OffersValidation
                                            validation={pageValidation.validations.offers}
                                            companyId={companyId}
                                            companyName={companyName}
                                            pageUrl={pageUrl}
                                            onHideOffer={(offerText, offerType) =>
                                                handleHideOffer(pageUrl, offerText, offerType)
                                            }
                                            onUnhideOffer={(offerText) =>
                                                handleUnhideOffer(pageUrl, offerText)
                                            }
                                            hiddenOffers={hiddenOffersByPage[pageUrl] || []}
                                        />
                                    )}

                                    {/* Tracking Numbers Validation */}
                                    {pageValidation.validations?.trackingNumbers && (
                                        <TrackingNumbersValidation validation={pageValidation.validations.trackingNumbers} />
                                    )}

                                    {/* Store Address Validation */}
                                    {pageValidation.validations?.contactInfo && (
                                        <StoreAddressValidation validation={pageValidation.validations.contactInfo} />
                                    )}

                                    {/* Counties Validation */}
                                    {pageValidation.validations?.counties && (
                                        <CountiesValidation validation={pageValidation.validations.counties} />
                                    )}
                                </div>

                                {/* Fine Print Validation - Full Width Section */}
                                <div className="mt-6">
                                    <FinePrintValidation
                                        validation={pageValidation.validations?.finePrint || {
                                            status: 'info',
                                            score: 0,
                                            message: 'No fine print validation data available',
                                            offerFinePrintMatches: [],
                                            missingFinePrint: [],
                                            extraFinePrint: [],
                                            bottomContainerValidation: null
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Critical Issues */}
            {validationResults.issues && validationResults.issues.length > 0 && (
                <div className="bg-gradient-to-r from-red-50 to-red-100/50 rounded-2xl p-6 border-2 border-red-200 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-red-100 rounded-xl border border-red-200">
                            <AlertTriangle size={24} className="text-red-600" />
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold text-red-900">Critical Issues</h3>
                            <p className="text-red-700 font-medium">
                                {validationResults.issues.length} issue{validationResults.issues.length !== 1 ? 's' : ''} requiring immediate attention
                            </p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        {validationResults.issues.map((issue, i) => (
                            <div key={i} className="bg-white border-2 border-red-200 rounded-2xl p-5 shadow-sm">
                                <div className="flex items-start gap-4">
                                    <div className="p-2 bg-red-100 rounded-xl border border-red-200 flex-shrink-0">
                                        <XCircle size={20} className="text-red-600" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between gap-3 mb-3">
                                            <div>
                                                <h4 className="font-bold text-lg text-red-900 mb-1">{issue.type}</h4>
                                                <div className="text-red-700 leading-relaxed">{issue.message}</div>
                                            </div>
                                            <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-bold flex-shrink-0">
                                                Issue #{i + 1}
                                            </div>
                                        </div>
                                        {issue.page && (
                                            <div className="bg-red-50/80 border border-red-200 rounded-xl p-3">
                                                <div className="flex items-center gap-2">
                                                    <Globe size={14} className="text-red-600" />
                                                    <span className="text-sm font-bold text-red-800">Page:</span>
                                                    <span className="text-sm text-red-700 font-mono bg-white px-2 py-1 rounded border">
                                                        {issue.page}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
